import React from 'react';

/**
 * SpecIcon — self-contained inline line-icons for the Trailer Details spec cards.
 *
 * Icons are decorative only (the spec label + value are real text in the DOM),
 * so each SVG is aria-hidden + focusable="false". Stroke uses currentColor so the
 * card CSS controls the color. Maps by the same key the spec builder emits; falls
 * back to a generic "spec" dot icon for any unmapped/new field.
 */

const S = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false',
};

const paths = {
  // Calendar
  year: <><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /><circle cx="12" cy="14" r="1.1" fill="currentColor" stroke="none" /></>,
  // Shield/badge (Make)
  make: <><path d="M12 2.5l7 3v5.5c0 4.6-3.1 7.6-7 9-3.9-1.4-7-4.4-7-9V5.5l7-3z" /></>,
  // Tag/label (Model)
  model: <><path d="M3 8.5v-3a2 2 0 012-2h3l11 11a2 2 0 010 2.8l-3.9 3.9a2 2 0 01-2.8 0L3 8.5z" /><circle cx="7.5" cy="8" r="1.2" /></>,
  // Ruler horizontal (Length)
  length: <><rect x="2.5" y="8.5" width="19" height="7" rx="1" /><path d="M7 8.5v3M11 8.5v4M15 8.5v3M19 8.5v4" /></>,
  // Ruler vertical / width arrows (Width)
  width: <><path d="M4 5v14M20 5v14" /><path d="M4 12h16M7 9l-3 3 3 3M17 9l3 3-3 3" /></>,
  // Weight / kettlebell (Weight Capacity)
  weight_capacity: <><path d="M8 8.5a4 4 0 118 0" /><path d="M6.5 8.5h11l1.6 10a2 2 0 01-2 2.3H6.9a2 2 0 01-2-2.3l1.6-10z" /></>,
  // Trailer side-profile (Trailer Type)
  trailer_type: <><path d="M2.5 15h15V9h-11l-4 3v3z" /><circle cx="7" cy="17.5" r="1.8" /><circle cx="13.5" cy="17.5" r="1.8" /><path d="M17.5 12h4" /></>,
  // Coupler + ball (Hitch Type)
  hitch_type: <><path d="M5 6h6l4 5" /><path d="M11 11.5a3 3 0 106 0 3 3 0 00-6 0z" /><path d="M14 14.5V18" /></>,
  // Hitch ball on shank (Ball Size)
  ball_size: <><circle cx="12" cy="8.5" r="4" /><path d="M12 12.5v4M9 20.5h6" /></>,
  // 7-way plug face (Light Plug Configuration)
  light_plug_configuration: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="7.5" r="0.9" fill="currentColor" stroke="none" /><circle cx="8.3" cy="10.3" r="0.9" fill="currentColor" stroke="none" /><circle cx="15.7" cy="10.3" r="0.9" fill="currentColor" stroke="none" /><circle cx="9" cy="14.4" r="0.9" fill="currentColor" stroke="none" /><circle cx="15" cy="14.4" r="0.9" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none" /><circle cx="12" cy="16.6" r="0.9" fill="currentColor" stroke="none" /></>,
  // Generic fallback
  spec: <><circle cx="12" cy="12" r="8.5" /><path d="M12 8v4M12 15.5h.01" /></>,
};

// Aliases so both snake_case and camelCase keys resolve.
const alias = {
  trailerType: 'trailer_type',
  hitchType: 'hitch_type',
  ballSize: 'ball_size',
  lightPlugConfiguration: 'light_plug_configuration',
  weight: 'weight_capacity',
  weightCapacity: 'weight_capacity',
};

const SpecIcon = ({ name }) => {
  const key = paths[name] ? name : alias[name] || 'spec';
  return <svg {...S}>{paths[key] || paths.spec}</svg>;
};

export default SpecIcon;
